import { StatusTag } from "@/components/common";
import { Tooltip } from "@/components/ui";
import { useMarketCalendar } from "@/hooks/api";
import { describeMarket } from "@/lib/market-session";
import type { MarketCalendar } from "@/types/api";

/**
 * 장 상태 — "규칙이 왜 안 돌았지" 의 가장 흔한 답을 헤더에서 끝냅니다.
 *
 * 지금까지 휴장·장전은 **서버만 알고 화면은 침묵**했어요. 그래서 운영자가 실행 이력과 로그를
 * 뒤져야 알 수 있었습니다. 이 값은 `GET /market/calendar/{country}` 에서 오고, 서버가
 * `MarketSession` 문서로 캐시하고 있어서 상류(토스) 호출이 늘지 않아요 — `MARKET_INFO` 는
 * 3 TPS 뿐입니다.
 *
 * 국내·미국을 **따로** 그립니다. 미국 정규장은 KST 22:30~05:00 이라 하나로 뭉개면 한국 새벽에
 * "장 마감" 만 떠 있는데 그 시각 `market: US` 규칙은 정규장에서 돌고 있어요.
 *
 * 라벨·톤 판정은 `lib/market-session.ts`(순수 함수 + 테스트)에 있습니다.
 */
const MARKETS: MarketCalendar["country"][] = ["KR", "US"];

function MarketTag({ country }: { country: MarketCalendar["country"] }) {
  const calendar = useMarketCalendar(country);
  const data = calendar.data;
  if (!data) return null;

  const view = describeMarket(data);
  /*
    남은 시간은 툴팁에만 둡니다. 헤더 한 줄은 1060px(`--layout-max-width`)뿐인데 탭 6개와
    배지 4종이 이미 그 폭을 다 쓰고 있어서, "마감까지 3시간 2분" 두 개를 인라인으로 세우면
    남은 폭이 모자라 탭이 글자 단위로 줄바꿈됐어요(운영자 스크린샷의 그 증상).
  */
  // 헤더 최상단이라 위로 띄우면 뷰포트 밖으로 잘립니다 — 아래로 엽니다.
  return (
    <Tooltip label={view.tail ? `${view.tail} · ${view.hint}` : view.hint} placement="bottom">
      <span className="inline-flex shrink-0 items-center whitespace-nowrap">
        <StatusTag color={view.tone} variant="weak" size="small">
          {view.label}
        </StatusTag>
      </span>
    </Tooltip>
  );
}

export function MarketStatusBadge() {
  return (
    <span className="inline-flex items-center gap-2">
      {MARKETS.map((country) => (
        <MarketTag key={country} country={country} />
      ))}
    </span>
  );
}
